import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import Button from "@material-ui/core/Button";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import { formatCurrency } from "app/main/utils/tableUtils";
import { useContext } from "react";
import { StrategyActionContext, StrategyStateContext } from "../../providers/StrategyContextProvider";
import { addToCart, removeFromCart } from "../../providers/reducer/actions";
import { useMediaQuery, useTheme } from "@material-ui/core";

const getInitVals = (keys, intiVal = 0) => keys.reduce((acc, key) => ({ ...acc, [key]: intiVal }), {});

const getRange = (value, formatter) => {
	const [min, max] = value;
	return `${formatter ? formatter(min) : min} - ${formatter ? formatter(max) : max}`;
};

const getTotalCumulatives = (selectedMoves) => {
	const totalCumulativeKeys = ["loaded_miles", "empty_miles", "profit", "duration", "min_payment", "max_payment"];
	return selectedMoves?.reduce((total, move, i, arr) => {
		totalCumulativeKeys.forEach((key) => {
			if (arr.length === 1) {
				total[key] = 0;
			} else if (i) {
				// First item skipped
				total[key] += move.data.stop[key];
			}
		});
		return total;
	}, getInitVals(totalCumulativeKeys));
};

const getTotalAverages = (selectedMoves) => {
	const totalAverageKeys = ["min_rpm", "max_rpm", "avg_volume"];

	return selectedMoves?.reduce((total, move, i, arr) => {
		totalAverageKeys.forEach((key) => {
			if (arr.length === 1) {
				total[key] = 0;
			} else if (i) {
				// First item skipped
				total[key] += move.data.stop[key] / (arr.length - 1);
			}
		});
		return total;
	}, getInitVals(totalAverageKeys));
};

const CartItem = ({ label, value, subValue }) => {
	const theme = useTheme();
	const largeScreen = useMediaQuery(theme.breakpoints.up("lg"));
	return (
		<Box style={{ display: "flex", justifyContent: "space-between" }}>
			<Typography variant={largeScreen ? "subtitle1" : "subtitle2"}>{label}</Typography>
			<Box>
				<Typography variant={largeScreen ? "subtitle1" : "subtitle2"}>{value}</Typography>
				{subValue && largeScreen && (
					<Typography variant="caption" style={{ display: "block", textAlign: "right" }}>
						{subValue}
					</Typography>
				)}
			</Box>
		</Box>
	);
};
const AddToCartButton = ({ selectedMoves, handler }) => (
	<Button
		color="secondary"
		variant="contained"
		disabled={selectedMoves.length <= 1}
		onClick={handler}
		style={{ margin: "5px 0", alignSelf: "center", padding: "5px 10px" }}
	>
		Approve Contract
	</Button>
);
const UpdateButton = ({ updated, selectedMoves, handler }) => (
	<Button
		color="secondary"
		variant="contained"
		disabled={!updated || selectedMoves.length <= 1}
		onClick={handler}
		style={{ margin: "5px 0", alignSelf: "center", padding: "5px 10px" }}
	>
		Update
	</Button>
);
const RemoveButton = ({ handler }) => (
	<Button
		color="primary"
		variant="contained"
		onClick={handler}
		style={{ margin: "5px 0", alignSelf: "center", padding: "5px 10px" }}
	>
		Remove
	</Button>
);

const TotalCard = ({ title, selectedMoves, allMoves, isSaved }) => {
	const { selectedPaths } = useContext(StrategyStateContext);
	const dispatch = useContext(StrategyActionContext);

	const theme = useTheme();
	const largeScreen = useMediaQuery(theme.breakpoints.up("lg"));

	const totalsCummulative = getTotalCumulatives(selectedMoves);
	const totalsAverages = getTotalAverages(selectedMoves);

	const totalValues = {
		...totalsCummulative,
		...totalsAverages,
		rpm: totalsCummulative.payment / (totalsCummulative.loaded_miles + totalsCummulative.empty_miles),
		max_rpm: totalsCummulative.max_payment / (totalsCummulative.loaded_miles + totalsCummulative.empty_miles),
		min_rpm: totalsCummulative.min_payment / (totalsCummulative.loaded_miles + totalsCummulative.empty_miles),
		duration: Math.ceil((totalsCummulative.loaded_miles + totalsCummulative.empty_miles) / 500),
		total_miles: totalsCummulative.loaded_miles + totalsCummulative.empty_miles,
	};

	// Access pathId located in last node
	const pathId = allMoves?.[allMoves.length - 1]?.data?.stop?.pathId;
	const inCart = !!selectedPaths[pathId];
	const updated = inCart && selectedPaths[pathId].length !== selectedMoves.length;
	// Get equipment
	const equipment = selectedMoves?.[0]?.data?.stop?.equipment;
	// Get available_date from the first move
	const available_date = selectedMoves?.[0]?.data?.stop?.available_date || new Date();
	// Calculate end_date by getting the last move's end_date
	const end_date = new Date(selectedMoves?.[selectedMoves.length - 1]?.data?.stop?.end_date);

	// Format dates
	const formatDate = (date) => {
		if (!(date instanceof Date) && typeof date === "string") {
			date = new Date(date);
		}
		return date instanceof Date ? date.toLocaleDateString() : "N/A";
	};

	const handleAddToCart = (e) => {
		e.stopPropagation();
		dispatch(addToCart(selectedMoves, pathId));
	};

	const handleRemoveFromCart = (e) => {
		e.stopPropagation();
		dispatch(removeFromCart(pathId));
	};

	return (
		<Box
			sx={{
				minWidth: largeScreen ? 270 : 150,
				marginLeft: "auto",
			}}
		>
			<Card
				variant="outlined"
				style={{
					transition: "filter 500ms linear",
					boxSizing: "border-box",
				}}
			>
				<CardContent style={{ paddingBottom: "10px" }}>
					<Typography variant={largeScreen ? "h5" : "subtitle1"} component="div" sx={{ fontWeight: 400 }}>
						{title + " " + equipment}
					</Typography>
					<Typography variant={"subtitle2"} component="div" sx={{ fontWeight: 100 }}>
						{`${!largeScreen ? " Averages" : ""}`}
					</Typography>
					<Box style={{ marginTop: "0.5rem", display: "flex", flexDirection: "column", fontSize: "0.5rem" }}>
						<CartItem
							label="Est. Revenue"
							value={formatCurrency((totalValues["min_payment"] + totalValues["max_payment"]) / 2, 0)}
							//subValue={getRange([totalValues["min_payment"], totalValues["max_payment"]], (payment) =>
							//	formatCurrency(payment, 0)
							//)}
						/>
						<CartItem label="Est. Miles" value={`${totalValues["total_miles"] || 0} mi`} />
						<CartItem
							label="Est. RPM"
							value={formatCurrency((totalValues["max_rpm"] + totalValues["min_rpm"]) / 2)}
							//subValue={getRange([totalValues["min_rpm"], totalValues["max_rpm"]], formatCurrency)}
						/>
						<CartItem label="Duration" value={`${totalValues["duration"] || "-"} days`} />
						<CartItem label="Starts in" value={formatDate(available_date)} />
						<CartItem label="Ends in" value={formatDate(end_date)} />
						{!isSaved &&
							(inCart ? (
								<Box style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
									<UpdateButton updated={updated} selectedMoves={selectedMoves} handler={handleAddToCart} />
									<RemoveButton handler={handleRemoveFromCart} />
								</Box>
							) : (
								<AddToCartButton selectedMoves={selectedMoves} handler={handleAddToCart} />
							))}
					</Box>
				</CardContent>
			</Card>
		</Box>
	);
};

export default TotalCard;
